import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Truck,
  Package,
  MapPin,
  CheckCircle,
  DollarSign,
  AlertCircle,
  XCircle,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { markOrderDelivered } from './api/Label';
import { products } from '../products';
import { Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const auth = getAuth();
const gold = '#DBAE6E';

const SANDBOX_MODE = false;
const BASE_URL = SANDBOX_MODE ? 'https://api-test.envia.com' : 'https://api.envia.com';
const API_TOKEN = '93611e1330421b790ade63b030b4aa4fb4350eb5aab5b491d417f77eae2f627a';
const HEADERS = {
  'Content-Type': 'application/json',
  Authorization: `Bearer ${API_TOKEN}`,
};

const trackShipments = async (trackingNumbers) => {
  try {
    if (!trackingNumbers?.length) throw new Error('No tracking numbers provided');
    const payload = { trackingNumbers: trackingNumbers.map(String) };
    const response = await axios.post(`${BASE_URL}/ship/generaltrack/`, payload, { headers: HEADERS });
    if (response.data.meta !== 'generaltrack') throw new Error('Invalid tracking response');
    return response.data.data;
  } catch (error) {
    console.error('Tracking Error:', error);
    return null;
  }
};

const DetailedOrderView = ({ order, setSelectedOrder }) => {
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [trackingData, setTrackingData] = useState(order.trackingDetails || []);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState(null);
  const [isExchangeOpen, setIsExchangeOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [exchangeError, setExchangeError] = useState(null);
  const [isExchangingLoading, setIsExchangingLoading] = useState(false);
  const [isCancelExchangeLoading, setIsCancelExchangeLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrackingData = async () => {
      if (order.trackingDetails && order.trackingDetails.length > 0) {
        setTrackingLoading(true);
        const trackingNumbers = order.trackingDetails.map(t => t.trackingNumber);
        const data = await trackShipments(trackingNumbers);
        if (data) {
          // Merge the latest tracking data with existing tracking details
          const updatedTrackingData = order.trackingDetails.map((tracking) => {
            const updated = data.find(d => d.trackingNumber === tracking.trackingNumber);
            return updated ? { ...tracking, ...updated } : tracking;
          });
          setTrackingData(updatedTrackingData);
        }
        setTrackingLoading(false);
        // Select the first tracking by default if available
        if (order.trackingDetails.length > 0) {
          setSelectedTracking(order.trackingDetails[0].trackingNumber);
        }
      } else {
        setTrackingData([]);
        setSelectedTracking(null);
      }
    };
    fetchTrackingData();
  }, [order.trackingDetails]);

  const getStatusStyle = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'delivered':
        return 'bg-green-700 text-black dark:text-white';
      case 'shipped':
        return 'bg-blue-700 text-black dark:text-white';
      case 'placed':
        return 'bg-yellow-500 text-black';
      case 'cancelled':
        return 'bg-red-700 text-black dark:text-white';
      case 'dispatched':
        return 'bg-yellow-700 text-black dark:text-white';
      case 'exchanging':
        return 'bg-violet-700 text-black dark:text-white';
      case 'returning':
        return 'bg-yellow-700 text-black dark:text-white';
      case 'returned':
        return 'bg-orange-700 text-black dark:text-white';
      case 'exchange':
        return 'bg-purple-700 text-black dark:text-white';
      case 'exchanged':
        return 'bg-pink-700 text-black dark:text-white';
      default:
        return 'bg-gray-600 text-black dark:text-white';
    }
  };

  const isSameDay = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    return today.toDateString() === checkDate.toDateString();
  };

  const isExchangeWindowOpen = () => {
    if (!order.deliveredAt) return false;
    const deliveryDate = new Date(order.deliveredAt);
    const currentDate = new Date();
    const daysSinceDelivery = Math.floor(
      (currentDate - deliveryDate) / (1000 * 60 * 60 * 24)
    );
    return daysSinceDelivery <= 3;
  };

  const canCancelExchange = () => {
    return order.orderStatus === 'Exchanging' && order.exchangeRequestedAt && isSameDay(order.exchangeRequestedAt);
  };

  const handleCancel = async () => {
    setCanceling(true);
    setCancelError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const orderRef = doc(db, 'Orders', user.email, 'UserOrders', order.orderId);
      await updateDoc(orderRef, {
        orderStatus: 'Cancelled',
        cancelledAt: new Date().toISOString(),
      });

      setSelectedOrder({ ...order, orderStatus: 'Cancelled' });

      navigate('/shop');
    } catch (error) {
      console.error('Error cancelling order:', error);
      setCancelError('Failed to cancel order. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const handleCancelExchange = async () => {
    setIsCancelExchangeLoading(true);
    setExchangeError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const orderRef = doc(db, 'Orders', user.email, 'UserOrders', order.orderId);
      await updateDoc(orderRef, {
        orderStatus: 'Delivered',
        exchangeFor: null,
        exchangeRequestedAt: null,
      });

      setSelectedOrder({
        ...order,
        orderStatus: 'Delivered',
        exchangeFor: null,
        exchangeRequestedAt: null,
      });
      navigate('/shop');
    } catch (error) {
      console.error('Error cancelling exchange:', error);
      setExchangeError('Failed to cancel exchange. Please try again.');
    } finally {
      setIsCancelExchangeLoading(false);
    }
  };

  const getProductDetails = () => {
    const product = products.find(p => p.name === order.items[0].name);
    return product || { sizes: [] };
  };

  const handleExchangeRequest = () => {
    setIsExchangeOpen(true);
  };

  const handleExchangeConfirm = async () => {
    setIsExchangingLoading(true);
    setExchangeError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      await markOrderDelivered(user.email, order.orderId);

      const orderRef = doc(db, 'Orders', user.email, 'UserOrders', order.orderId);
      await updateDoc(orderRef, {
        orderStatus: 'Exchanging',
        exchangeFor: selectedSize,
        exchangeRequestedAt: new Date().toISOString(),
      });

      setSelectedOrder({
        ...order,
        orderStatus: 'Exchanging',
        exchangeFor: selectedSize,
      });
      setIsExchangeOpen(false);
      setSelectedSize(null);
      navigate('/shop');
    } catch (error) {
      console.error('Error initiating exchange:', error);
      setExchangeError('Failed to initiate exchange. Please try again.');
    } finally {
      setIsExchangingLoading(false);
    }
  };

  const isCancelled = String(order.orderStatus || '').toLowerCase() === 'cancelled';
  const isPlaced = String(order.orderStatus || '').toLowerCase() === 'placed';
  const canCancel = isPlaced && isSameDay(order.createdAt);
  const isDelivered = String(order.orderStatus || '').toLowerCase() === 'delivered';
  const isExchanging = String(order.orderStatus || '').toLowerCase() === 'exchanging';
  const isExchanged = String(order.orderStatus || '').toLowerCase() === 'exchanged';
  const canExchange = (isDelivered || isExchanging) && isExchangeWindowOpen();

  const renderTrackingView = () => {
    if (trackingLoading) {
      return (
        <div className="bg-primary dark:bg-primary-dark p-6 rounded-xl shadow-sm text-sm text-black dark:text-white flex items-center justify-center">
          <Loader className="animate-spin text-gold mr-2" size={24} />
          Fetching tracking details...
        </div>
      );
    }

    if (!trackingData || trackingData.length === 0) {
      return (
        <div className="bg-primary dark:bg-primary-dark p-6 rounded-xl shadow-sm text-sm text-black dark:text-white">
          Tracking Not Available
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {trackingData.map((tracking, index) => (
          <motion.div
            key={index}
            className={`p-4 rounded-xl shadow-sm border text-left w-full bg-primary dark:bg-primary-dark ${
              selectedTracking === tracking.trackingNumber
                ? 'border-[#DBAE6E]'
                : 'border-neutral-700'
            } hover:border-[#DBAE6E] transition-colors cursor-pointer`}
            onClick={() =>
              setSelectedTracking(
                selectedTracking === tracking.trackingNumber ? null : tracking.trackingNumber
              )
            }
            whileHover={{ scale: 1.01 }}
            transition={{ type: 'spring', stiffness: 120 }}
          >
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <div className="text-sm font-semibold text-black dark:text-white">
                  Product: {tracking.content?.content || tracking.packages?.[0]?.content || 'N/A'}
                </div>
                <div className="text-xs text-black dark:text-white">
                  Shipment ID: {tracking.shipmentId || 'N/A'}
                </div>
                <div className="text-xs text-black dark:text-white">
                  Tracking: {tracking.trackingNumber || 'N/A'}
                </div>
                {tracking.trackUrl && (
                  <a
                    href={tracking.trackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#DBAE6E] text-xs inline-block hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Track Now
                  </a>
                )}
              </div>
              <div className="flex flex-col items-end space-y-2">
                {tracking.status ? (
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm whitespace-nowrap ${getStatusStyle(
                      tracking.status
                    )}`}
                  >
                    {tracking.status}
                  </span>
                ) : (
                  <span className="text-xs text-black dark:text-white">Status: N/A</span>
                )}
                {selectedTracking === tracking.trackingNumber ? (
                  <ChevronUp className="text-[#DBAE6E]" size={20} />
                ) : (
                  <ChevronDown className="text-[#DBAE6E]" size={20} />
                )}
              </div>
            </div>
            <AnimatePresence>
              {selectedTracking === tracking.trackingNumber && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border-t border-neutral-700 pt-4"
                >
                  <h4 className="text-md font-semibold text-black dark:text-white mb-2">Tracking History</h4>
                  {tracking.eventHistory && tracking.eventHistory.length > 0 ? (
                    <div className="space-y-2">
                      {tracking.eventHistory.map((event, idx) => (
                        <div
                          key={idx}
                          className="bg-neutral-900 p-3 rounded-lg text-sm text-gray-300"
                        >
                          <div className="flex justify-between">
                            <span>
                              {event.date ? new Date(event.date).toLocaleString() : 'N/A'}
                            </span>
                            <span>{event.status || 'N/A'}</span>
                          </div>
                          <div className="mt-1">
                            <span className="text-black dark:text-white">Location: </span>
                            {event.location || 'N/A'}
                          </div>
                          <div className="mt-1">
                            <span className="text-black dark:text-white">Description: </span>
                            {event.description || 'N/A'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-black dark:text-white">No tracking history available</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    );
  };

  const renderExchangeView = () => {
    const product = getProductDetails();

    if (!isExchangeOpen) {
      if (isExchanging) return null;
      return (
        <button
          onClick={handleExchangeRequest}
          className="w-full py-4 rounded-xl font-bold text-black bg-[#DBAE6E] hover:bg-[#e6c083] transition-colors shadow-md flex items-center justify-center relative overflow-hidden"
          disabled={isExchangingLoading}
        >
          {isExchangingLoading && (
            <span className="absolute inset-0 flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 text-black dark:text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z" />
              </svg>
            </span>
          )}
          <RefreshCw className="mr-2" size={20} /> Request Exchange
        </button>
      );
    }

    return (
      <div className="bg-primary dark:bg-primary-dark p-6 rounded-xl shadow-sm">
        <h4 className="text-md font-semibold text-black dark:text-white mb-4">Select New Size</h4>
        <div className="space-y-6">
          <div>
            <label className="text-sm text-black dark:text-white mb-2 block">Size</label>
            <div className="flex flex-wrap gap-3">
              {product.sizes.map(size => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-all 
                  ${
                    selectedSize === size
                      ? 'bg-[#DBAE6E] text-black shadow-lg'
                      : 'bg-neutral-700 text-black dark:text-white hover:bg-[#DBAE6E] hover:text-black'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
          {selectedSize && (
            <button
              onClick={handleExchangeConfirm}
              className="w-full py-4 rounded-xl font-bold text-black bg-[#DBAE6E] hover:bg-[#e6c083] transition-colors shadow-md flex items-center justify-center relative overflow-hidden"
              disabled={isExchangingLoading}
            >
              {isExchangingLoading && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <div className="h-8 w-8 border-4 border-t-transparent border-white rounded-full animate-spin"></div>
                </span>
              )}
              Confirm Exchange
            </button>
          )}
          {exchangeError && (
            <div className="mt-4 p-4 bg-red-800 border-l-4 border-red-500 text-black dark:text-white rounded shadow">
              <AlertCircle size={20} className="inline mr-2" /> {exchangeError}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-primary dark:bg-primary-dark py-12 px-4 sm:px-6 lg:px-12 text-black dark:text-white font-sans">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => setSelectedOrder(null)}
          className="inline-flex items-center text-secondary mb-8 text-lg font-bold"
        >
          <ArrowLeft className="mr-3" size={28} /> Back
        </button>

        <div className="bg-primray dark:bg-primary-dark dark-bg-neural-900 rounded-xl shadow-2xl border border-[#DBAE6E]/40 p-8 space-y-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-[#DBAE6E]">#{order.orderId}</p>
              <p className="text-sm text-black dark:text-white mt-1">
                {new Date(order.createdAt).toLocaleString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                }).replace(/\//g, '/')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center text-black dark:text-white">
                  <DollarSign className="mr-3 text-gold" size={20} />
                  <span>
                    {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Prepaid'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4 flex items-center text-black dark:text-white">
                <Package className="mr-3 text-gold" size={24} /> Items
              </h3>
              <div className="bg-primary dark:bg-primary-dark border border-neutral-700 rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-primary dark:bg-primary-dark">
                    <tr>
                      <th className="py-3 px-4 text-left text-gold font-semibold">Item</th>
                      <th className="py-3 px-4 text-right text-gold font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item, index) => (
                      <tr key={index} className="border-t border-neutral-800">
                        <td className="py-3 px-4 text-black dark:text-white">{`${item.name} (${item.size}, ${item.color}) x${item.quantity}`}</td>
                        <td className="py-3 px-4 text-right text-black dark:text-white">
                          ₹{parseFloat(item.declaredValue).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-neutral-800 bg-primary dark:bg-primary-dark">
                      <td className="py-3 px-4 font-bold text-black dark:text-white">Total</td>
                      <td className="py-3 px-4 text-right font-bold text-[#DBAE6E]">
                        ₹{parseFloat(order.totalPrice).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4 flex items-center text-black dark:text-white">
              <MapPin className="mr-3 text-gold" size={24} /> Shipping Address
            </h3>
            <div className="bg-primary dark:bg-primary-dark p-6 rounded-xl shadow-sm text-sm space-y-2 text-black dark:text-white">
              <div className="font-semibold">{order.shippingAddress.fullName}</div>
              <div className="text-gray-700 dark:text-gray-300">
                {[
                  order.shippingAddress.street,
                  order.shippingAddress.number,
                  order.shippingAddress.district,
                  order.shippingAddress.city,
                  order.shippingAddress.state,
                  order.shippingAddress.postalCode,
                  order.shippingAddress.country,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
              <div className="text-black dark:text-white">Phone: {order.shippingAddress.phone}</div>
              {order.shippingAddress.email && (
                <div className="text-black dark:text-white">
                  Email: {order.shippingAddress.email}
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4 flex items-center text-black dark:text-white">
              <Truck className="mr-3 text-gold" size={24} /> Tracking
            </h3>
            {renderTrackingView()}
          </div>

          <div className="space-y-4">
            {!isCancelled && !isExchanging && !isExchanged && (
              <>
                {cancelError && (
                  <div className="mb-4 p-4 bg-red-800 border-l-4 border-red-500 text-black dark:text-white rounded shadow">
                    <AlertCircle size={20} className="inline mr-2" /> {cancelError}
                  </div>
                )}
                {canCancel ? (
                  <button
                    onClick={handleCancel}
                    disabled={canceling}
                    className={`w-full py-4 rounded-xl font-bold text-black dark:text-white ${
                      canceling ? 'bg-gray-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                    } transition-colors shadow-md flex items-center justify-center relative overflow-hidden`}
                  >
                    {canceling && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <div className="h-8 w-8 border-4 border-t-transparent border-white rounded-full animate-spin"></div>
                      </span>
                    )}
                    {canceling ? 'Cancelling...' : 'Cancel Order'}
                  </button>
                ) : (
                  !isDelivered && (
                    <div className="bg-yellow-900 text-yellow-300 p-4 rounded-xl shadow-sm border border-yellow-600 flex items-center">
                      <XCircle className="mr-3" /> Cannot cancel order at this moment.
                    </div>
                  )
                )}
              </>
            )}

            {isExchanged && (
              <div className="bg-pink-800 text-black dark:text-white p-4 rounded-xl shadow-sm border border-pink-900 flex items-center">
                <CheckCircle className="mr-3" /> Exchange approved, new order is placed.
              </div>
            )}

            {canExchange && (
              <div>
                {renderExchangeView()}
                <p className="text-xs text-black dark:text-white mt-2 text-center">
                  Exchange window: 3 days from delivery (
                  {Math.floor(
                    (new Date() - new Date(order.deliveredAt)) / (1000 * 60 * 60 * 24)
                  )}{' '}
                  days elapsed)
                </p>
              </div>
            )}
            {(isDelivered || isExchanging) && !canExchange && !isExchanged && (
              <div className="bg-gray-800 text-black dark:text-white p-4 rounded-xl shadow-sm border border-gray-600 flex items-center">
                <XCircle className="mr-3" /> Exchange window closed (3 days elapsed)
              </div>
            )}
            {isExchanging && (
              <div className="bg-purple-800 text-black dark:text-white p-4 rounded-xl shadow-sm border border-purple-600 flex items-center justify-between">
                <div className="flex items-center">
                  <RefreshCw className="mr-3" /> Exchange in progress for size {order.exchangeFor}
                </div>
                {canCancelExchange() && (
                  <button
                    onClick={handleCancelExchange}
                    className={`p-2 rounded-full bg-red-600 hover:bg-red-700 text-black dark:text-white flex items-center justify-center relative overflow-hidden ${
                      isCancelExchangeLoading ? 'cursor-not-allowed' : ''
                    }`}
                    disabled={isCancelExchangeLoading}
                  >
                    {isCancelExchangeLoading && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <div className="h-8 w-8 border-4 border-t-transparent border-white rounded-full animate-spin"></div>
                      </span>
                    )}
                    <X size={20} className={isCancelExchangeLoading ? 'opacity-0' : ''} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedOrderView;